import * as React from 'react';
import { Button, Checkbox, Dropdown, Input, MessageBar, MessageBarBody, Option, Radio, RadioGroup, Spinner } from '@fluentui/react-components';
import { AddRegular, DeleteRegular } from '@fluentui/react-icons';
import { buildChecklist, FAMILY_LABELS, peopleProblems, PROGRAMS, type Answers, type IChecklistLine, type IProgram } from '../../api/src/programs';
import { ApiError, applicationOpened } from '../api';
import { errorText, useApp } from '../app/context';
import { UPGRADE_MAIL } from '../app/product';
import { gateProblems, type INewPerson, openApplication } from '../records/applications';
import { ageOn, type IClientView, localToday } from '../records/model';
import { useLoad } from '../records/useLoad';

/** Opening an application for a client: program, answers, people, who works on it, and the engagement gates. */
export default function NewApplication(props: { clientKey?: string }): React.ReactElement {
  const app = useApp();
  const firm = app.workspace.firm;
  const offered: IProgram[] = PROGRAMS.filter(p => !firm?.programs.length || firm.programs.indexOf(p.id) !== -1);
  const { data: clients, error: loadError } = useLoad(() => app.records.allClients().then(r => r.sort((a, b) => a.name.localeCompare(b.name))), [app.records]);
  const [clientKey, setClientKey] = React.useState<string>(props.clientKey ?? '');
  const [programId, setProgramId] = React.useState<string>('');
  const [answers, setAnswers] = React.useState<Answers>({});
  const [people, setPeople] = React.useState<INewPerson[]>([]);
  const [lawyer, setLawyer] = React.useState<string>(() => firm?.staff.filter(s => s.role === 'lawyer')[0]?.name ?? '');
  const [clerk, setClerk] = React.useState<string>(() => firm?.staff.filter(s => s.role === 'clerk')[0]?.name ?? '');
  const [retainerSigned, setRetainerSigned] = React.useState<string>('');
  const [representativeSigned, setRepresentativeSigned] = React.useState<string>('');
  const [restrictedChoice, setRestricted] = React.useState<boolean>(false);
  const [working, setWorking] = React.useState<string | undefined>(undefined);
  const [error, setError] = React.useState<React.ReactNode | undefined>(undefined);

  const client: IClientView | undefined = (clients ?? []).filter(c => c.key === clientKey)[0];
  const program: IProgram | undefined = offered.filter(p => p.id === programId)[0];
  // A restricted client's files are always restricted, so their name never reaches the main library.
  const restricted: boolean = restrictedChoice || client?.restricted === true;

  // A new program: fresh answers, and its required people, the client first.
  const choose = (id: string): void => {
    const p: IProgram | undefined = offered.filter(x => x.id === id)[0];
    setProgramId(id);
    setAnswers({});
    setRestricted(p?.restricted === true || p?.family === 'refugee');
    // The client goes in first: as the sponsor in a family sponsorship or sponsorship appeal (one sponsor), otherwise as the applicant.
    const clientRole: string = (p?.roles ?? []).some(r => r.id === 'sponsor' && r.max === 1) ? 'sponsor' : 'principal';
    const list: INewPerson[] = [];
    for (const r of p?.roles ?? []) {
      for (let i = 0; i < r.min; i++) {
        const isClient: boolean = r.id === clientRole && i === 0;
        list.push({ role: r.id, name: isClient && client ? client.name : '', birthDate: isClient ? client?.birthDate : undefined });
      }
    }
    setPeople(list);
  };

  const named: INewPerson[] = people.filter(p => p.name.trim());
  const today: string = localToday();
  const lines: IChecklistLine[] = program
    ? buildChecklist(program, answers, named.map((p, i) => ({ key: String(i), role: p.role, name: p.name.trim(), age: ageOn(p.birthDate, today) })))
    : [];
  const unanswered: string[] = (program?.questions ?? []).filter(q => answers[q.id] === undefined).map(q => q.text);
  const gates: string[] = client ? gateProblems({ client, retainerSigned, representativeSigned }) : ['Choose the client.'];
  const peopleIssues: string[] = program ? peopleProblems(program, named.map((p, i) => ({ key: String(i), role: p.role, name: p.name }))) : [];
  const restrictedReady: boolean = !!app.records.restricted;
  const ready: boolean = !!program && !unanswered.length && !gates.length && !peopleIssues.length && !working && (!restricted || restrictedReady);

  const open = async (): Promise<void> => {
    if (!client || !program) {
      return;
    }
    setError(undefined);
    try {
      // A restricted file keeps its client in the restricted clients list too.
      const where: IClientView = restricted && !client.restricted ? await app.records.moveToRestricted(client) : client;
      const r = await openApplication(
        { graph: app.graph, manageGraph: app.manageGraph, audit: app.audit, workspace: app.workspace, records: app.records, user: app.account.username, countOpened: applicationOpened },
        { client: where, program, answers, people: named.map(p => ({ ...p, name: p.name.trim() })), lawyer, clerk, retainerSigned, representativeSigned, restricted },
        setWorking
      );
      app.refreshPlan();
      window.location.hash = `/applications/${encodeURIComponent(r.key)}`;
    } catch (e) {
      setError(e instanceof ApiError && e.upgrade ? <>{e.message} <a href={UPGRADE_MAIL}>Upgrade</a></> : errorText(e));
      setWorking(undefined);
    }
  };

  const staff = firm?.staff ?? [];
  const update = (i: number, change: Partial<INewPerson>): void => setPeople(list => list.map((p, j) => (j === i ? { ...p, ...change } : p)));

  return (
    <main className="page" style={{ maxWidth: 960 }}>
      <div className="page-head"><div><h1>Open an application</h1><p><a href="#/applications">Applications</a></p></div></div>
      {(error || loadError) && <MessageBar intent="error"><MessageBarBody>{error ?? loadError}</MessageBarBody></MessageBar>}

      <section className="card" style={{ gap: 12 }}>
        <h2>Client and program</h2>
        <div className="form-grid">
          <label>Client
            <Dropdown placeholder={clients ? 'Choose a client' : 'Loading…'} value={client?.name ?? ''} selectedOptions={clientKey ? [clientKey] : []} onOptionSelect={(_, d) => setClientKey(d.optionValue ?? '')}>
              {(clients ?? []).map(c => <Option key={c.key} value={c.key}>{c.name}</Option>)}
            </Dropdown>
          </label>
          <label>Program
            <Dropdown placeholder="Choose a program" value={program?.name ?? ''} selectedOptions={programId ? [programId] : []} onOptionSelect={(_, d) => choose(d.optionValue ?? '')}>
              {offered.map(p => <Option key={p.id} value={p.id} text={p.name}>{`${p.name}${p.status === 'paused' ? ' (intake paused)' : ''} · ${FAMILY_LABELS[p.family]}`}</Option>)}
            </Dropdown>
          </label>
        </div>
        <span className="small">No client yet? <a href="#/clients/new">Add the client</a> first: the conflict check runs then.</span>
        {program?.status === 'paused' && (
          <MessageBar intent="warning"><MessageBarBody>Intake for this program is paused: {program.pausedNote} Open a file only for an application already invited or sent before the pause.</MessageBarBody></MessageBar>
        )}
        {program && (
          <span className="small">
            Checklist follows IRCC's pages of {program.version}{program.review === 'pending' ? ' (draft: not yet reviewed by a lawyer; check it against IRCC\'s current list)' : ''}.{' '}
            {program.sources.map((s, i) => <span key={i}><a href={s.url} target="_blank" rel="noreferrer">{s.title}</a>{i < program.sources.length - 1 ? '; ' : ''}</span>)}
          </span>
        )}
      </section>

      {program && (
        <section className="card" style={{ gap: 14 }}>
          <h2>About this file</h2>
          {program.questions.map(q => (
            <div key={q.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <strong style={{ fontSize: 14 }}>{q.text}</strong>
              {q.kind === 'yesno'
                ? (
                  <RadioGroup layout="horizontal" value={answers[q.id] === undefined ? '' : answers[q.id] ? 'yes' : 'no'} onChange={(_, d) => setAnswers(a => ({ ...a, [q.id]: d.value === 'yes' }))}>
                    <Radio value="yes" label="Yes" /><Radio value="no" label="No" />
                  </RadioGroup>
                )
                : (
                  <Dropdown placeholder="Choose" value={String(answers[q.id] ?? '')} selectedOptions={answers[q.id] !== undefined ? [String(answers[q.id])] : []} onOptionSelect={(_, d) => setAnswers(a => ({ ...a, [q.id]: d.optionValue }))} style={{ maxWidth: 360 }}>
                    {(q.choices ?? []).map(c => <Option key={c} value={c}>{c}</Option>)}
                  </Dropdown>
                )}
            </div>
          ))}
        </section>
      )}

      {program && (
        <section className="card" style={{ gap: 10 }}>
          <h2>People on the application</h2>
          <span className="small" style={{ fontSize: 14 }}>Dates of birth decide which forms each person needs (some are only for people 18 or older).</span>
          {people.map((p, i) => {
            const role = program.roles.filter(r => r.id === p.role)[0];
            const canRemove: boolean = people.filter(x => x.role === p.role).length > (role?.min ?? 0);
            return (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '170px minmax(0, 1.5fr) 160px auto', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{role?.label ?? p.role}</span>
                <Input aria-label={`${role?.label ?? p.role}: name`} placeholder="Last name, first name" value={p.name} onChange={(_, d) => update(i, { name: d.value })} />
                <Input aria-label={`${role?.label ?? p.role}: date of birth`} type="date" value={p.birthDate ?? ''} onChange={(_, d) => update(i, { birthDate: d.value || undefined })} />
                <Button appearance="subtle" icon={<DeleteRegular />} aria-label="Remove" disabled={!canRemove} onClick={() => setPeople(list => list.filter((_, j) => j !== i))} />
              </div>
            );
          })}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {program.roles.filter(r => people.filter(p => p.role === r.id).length < r.max).map(r => (
              <Button key={r.id} icon={<AddRegular />} onClick={() => setPeople(list => [...list, { role: r.id, name: '' }])}>Add {r.label.toLowerCase()}</Button>
            ))}
          </div>
          {peopleIssues.map(m => <span key={m} className="small" style={{ color: 'var(--red)' }}>{m}</span>)}
        </section>
      )}

      {program && (
        <section className="card" style={{ gap: 12 }}>
          <h2>Who works on it, and engagement</h2>
          <div className="form-grid">
            <label>Lawyer
              <Dropdown value={lawyer} selectedOptions={[lawyer]} onOptionSelect={(_, d) => setLawyer(d.optionValue ?? '')}>
                {staff.filter(s => s.role === 'lawyer').map(s => <Option key={s.name} value={s.name}>{s.name}</Option>)}
              </Dropdown>
            </label>
            <label>Clerk
              <Dropdown value={clerk} selectedOptions={[clerk]} onOptionSelect={(_, d) => setClerk(d.optionValue ?? '')}>
                <Option value="">No clerk</Option>
                {staff.filter(s => s.role === 'clerk').map(s => <Option key={s.name} value={s.name}>{s.name}</Option>)}
              </Dropdown>
            </label>
            <label>Retainer signed on<Input type="date" max={today} value={retainerSigned} onChange={(_, d) => setRetainerSigned(d.value)} /></label>
            <label>Use of a Representative (IMM 5476) signed on<Input type="date" max={today} value={representativeSigned} onChange={(_, d) => setRepresentativeSigned(d.value)} /></label>
          </div>
          {client && (
            <span className="small" style={{ fontSize: 14 }}>
              Conflict check: {client.conflictOn ? `recorded ${new Date(client.conflictOn).toLocaleDateString()}` : 'not recorded'} ·
              Identity verified: {client.idOn ? `${client.idOn}` : 'not recorded'} · <a href={`#/clients/${encodeURIComponent(client.key)}`}>Client page</a>
            </span>
          )}
          {gates.map(g => <span key={g} className="small" style={{ color: 'var(--red)' }}>{g}</span>)}
          <Checkbox checked={restricted} disabled={client?.restricted === true} onChange={(_, x) => setRestricted(!!x.checked)}
            label={<span><strong>Restricted file</strong>: kept in the restricted library and lists, open only to your "Restricted files" group. Refugee and private-sponsorship files are restricted by default.</span>} />
          {restricted && client && !client.restricted && restrictedReady && <span className="small">{client.name}'s record moves to the restricted clients list when you open this file. Entries about the client already in the main History stay there.</span>}
          {restricted && !restrictedReady && <span className="small" style={{ color: 'var(--red)' }}>Restricted files aren't set up yet (or you're not in the group). A site owner can set them up in Settings.</span>}
        </section>
      )}

      {program && (
        <section className="card" style={{ gap: 10 }}>
          <h2>Checklist preview: {lines.length} item{lines.length === 1 ? '' : 's'}</h2>
          {unanswered.length > 0 && <span className="small">Answer the questions above to see the whole checklist.</span>}
          <details>
            <summary className="small">Show the items</summary>
            <ul className="steps-list" style={{ fontSize: 14 }}>
              {lines.map(l => <li key={`${l.itemId}|${l.personKey}`}>{l.section}: {l.label}{l.form ? ` (${l.form})` : ''}</li>)}
            </ul>
          </details>
          <span className="small" style={{ fontSize: 14 }}>Opening creates the application's folders under Clients, and its checklist in your lists. You can undo it from History.</span>
          {working
            ? <Spinner size="small" label={working} labelPosition="after" />
            : <Button appearance="primary" disabled={!ready} onClick={() => { void open(); }} style={{ alignSelf: 'flex-start' }}>Open the application</Button>}
        </section>
      )}
    </main>
  );
}
